import React, {useEffect, useState} from 'react';
import qs from 'query-string';
import _ from 'lodash';
import {
  PencilAltIcon,
  EyeOffIcon,
  RefreshIcon,
  BanIcon,
  SparklesIcon,
  ThumbUpIcon,
  ThumbDownIcon,
} from '@heroicons/react/outline';

import Link from '../components/Link';
import Modal from '../components/ModalNew';
import StarRating from '../components/StarRating';
import Button, {ButtonSizes} from '../components/ButtonNew';
import {Tabs} from '../components/Tabs';
import RatingWidget from '../components/RatingWidget';
import {Spinner} from '../components/Spinner';
import LessonThumbnail from '../components/LessonThumbnail';
import VideoBadge from '../components/VideoBadge';
import {MetaRow} from '../pages/QuestionListPage/ListItem';
import {StyledCheckbox, StyledTextArea} from '../components/Forms';
import {TranscriptItems} from './LiveTranscript';
import {
  shouldHideCoaching,
  getDisplayTimeForPreviousDuetSession,
  toDuetInterviewTypeName,
  hideAIFeedbackFeatures,
  getResizedLessonThumbnail,
  cx,
} from '../util';
import {
  InterviewTypes,
  InterviewModes,
  FeedbackFields,
  AIFeedbackStatus,
} from '../../../lib/constants/duet';
import {useAppContext, useAnalytics} from '../hooks';
import {rateAIFeedback} from '../api/api';

/**
 * All of these arrays of *Fields should be treated
 * as APPEND ONLY, so that we don't accidentally hide
 * deprecated feedback fields. Fields appear in the order
 * that they will be displayed in the modal.
 *
 * v2 fields represent the move from 4 stars to 5 stars
 */
const AlgorithmsFields = [
  FeedbackFields.problem_solving,
  FeedbackFields.problem_solving_v2,
  FeedbackFields.coding,
  FeedbackFields.coding_v2,
  FeedbackFields.communication,
  FeedbackFields.communication_v2,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer,
  FeedbackFields.interviewer_v2,
];

const PMFields = [
  FeedbackFields.improve,
  FeedbackFields.communication_v2,
  FeedbackFields.product_vision,
  FeedbackFields.prioritization,
  FeedbackFields.business_strategy,
  FeedbackFields.data_literacy,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const SystemDesignFields = [
  FeedbackFields.improve,
  FeedbackFields.problem_scope,
  FeedbackFields.communication_v2,
  FeedbackFields.technical_depth,
  FeedbackFields.scalability,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const BehavioralFields = [
  FeedbackFields.improve,
  FeedbackFields.communication_v2,
  FeedbackFields.competency_v2,
  FeedbackFields.collaboration,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const BehavioralIBFields = [
  FeedbackFields.story_quality,
  FeedbackFields.story_structure,
  FeedbackFields.banking_connection,
  FeedbackFields.story_delivery,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const SQLFields = [
  FeedbackFields.problem_scope,
  FeedbackFields.coding_v2,
  FeedbackFields.communication_v2,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const DataScienceMLFields = [
  FeedbackFields.problem_solving_v2,
  FeedbackFields.problem_scope,
  FeedbackFields.data_literacy,
  FeedbackFields.communication_v2,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const FrontendFields = [
  FeedbackFields.problem_solving_v2,
  FeedbackFields.coding_v2,
  FeedbackFields.communication_v2,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const InvestmentBankingFields = [
  FeedbackFields.accuracy,
  FeedbackFields.structure,
  FeedbackFields.delivery,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer_v2,
];

const PrampTechnicalFields = [
  FeedbackFields.problem_solving,
  FeedbackFields.communication,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer,
];

const PrampBehavioralFields = [
  FeedbackFields.competency,
  FeedbackFields.communication,
  FeedbackFields.interpersonal_skills,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer,
];

const PrampDataScienceFields = [
  FeedbackFields.problem_solving,
  FeedbackFields.domain_knowledge,
  FeedbackFields.communication,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer,
];

const PrampProductFields = [
  FeedbackFields.competency,
  FeedbackFields.communication,
  FeedbackFields.do_well,
  FeedbackFields.not_good,
  FeedbackFields.interviewer,
];

const interviewTypeToFields = {
  [InterviewTypes.Algorithms.key]: AlgorithmsFields,
  [InterviewTypes.PM.key]: PMFields,
  [InterviewTypes.Behavioral.key]: BehavioralFields,
  [InterviewTypes['Behavioral-IB'].key]: BehavioralIBFields,
  [InterviewTypes['System-Design'].key]: SystemDesignFields,
  [InterviewTypes.SQL.key]: SQLFields,
  [InterviewTypes['Data-Science-ML'].key]: DataScienceMLFields,
  [InterviewTypes.Frontend.key]: FrontendFields,
  [InterviewTypes['Investment-Banking'].key]: InvestmentBankingFields,
};

const prampInterviewTypeToFields = {
  algorithms: AlgorithmsFields,
  system_design: PrampTechnicalFields,
  frontend: PrampTechnicalFields,
  behavioral: PrampBehavioralFields,
  applied_data_science: PrampDataScienceFields,
  product_case_study: PrampProductFields,
};

const TabKeys = {
  PEER: 'peer',
  AI: 'ai',
  TRANSCRIPT: 'transcript',
};
const PeerTab = {key: TabKeys.PEER, label: 'Peer feedback'};
const AITab = {key: TabKeys.AI, label: 'AI feedback'};
const TranscriptTab = {key: TabKeys.TRANSCRIPT, label: 'Transcript'};

const getFeedbackFields = (session) => {
  const fields = session.mode === InterviewModes.Pramp
    ? prampInterviewTypeToFields[session.type]
    : interviewTypeToFields[session.type];

  return fields || [];
};

const getCoachingUrl = (session) => {
  const typeToQuery = {
    product_management: {role: 'pm'},
    algorithms: {skill: 'dsa'},
    system_design: {skill: 'system-design-skill'},
  };

  return qs.stringifyUrl({
    url: '/coaching',
    query: {
      src: 'aced-practice-feedback',
      ...typeToQuery[session.type],
    },
  });
};

const ScoreBar = ({score}) => {
  const scoreColor = [null, 'bg-red-500', 'bg-red-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-500'];

  return (
    <div className="w-full flex items-center">
      <div className="flex grow space-x-0.5">
        {[1, 2, 3, 4, 5].map((bar) => {
          const shouldColor = score && bar <= score;

          return (
            <div className={cx(
              'grow h-3 first:rounded-bl-full first:rounded-tl-full last:rounded-br-full last:rounded-tr-full',
              {[scoreColor[score]]: shouldColor, 'bg-gray-200': !shouldColor},
            )}
            />
          );
        })}
      </div>

      <div className="text-sm font-semibold ml-3">
        {_.isNumber(score) ? `${score}/5` : 'N/A'}
      </div>
    </div>
  );
};

const BlurredAIFeedback = () => (
  <div
    className="border border-gray-200 rounded-lg overflow-scroll flex flex-col items-start text-left p-4 space-y-3 mb-3 select-none"
    style={{maxHeight: '50vh'}}
  >
    <h3 className="text-lg font-semibold text-gray-900">
      Summary
    </h3>
    <div className="text-gray-500 blur-sm">
      This is example AI feedback, sorry! Your ability to collaborate in
      setting up the interview process was evident. However, for
      problem-solving, consider articulating more specific strategies
      to tackle the areas you want to improve.
    </div>
    <h3 className="text-lg font-semibold text-gray-900">Collaboration</h3>
    <div className="blur-sm">
      <ScoreBar score={3} />
      <p className="text-gray-500">
        You actively engaged in setting up the interview structure
        collaboratively, showing a willingness to adapt to the
        format and partner with your peer.
      </p>
    </div>
    <h3 className="text-lg font-semibold text-gray-900">Communication</h3>
    <div className="blur-sm">
      <ScoreBar score={5} />
      <p className="text-gray-500">
        You communicated clearly and maintained a friendly tone
        throughout the conversation. However, there were moments
        where you could have been more concise.
      </p>
    </div>
    <h3 className="text-lg font-semibold text-gray-900">Problem solving</h3>
    <div className="blur-sm">
      <ScoreBar score={5} />
      <p className="text-gray-500">
        You identified the areas you want to improve, such as going
        deeper into product sense questions, but did not elaborate
        on specific strategies for improvement.
      </p>
    </div>
  </div>
);

// TODO: Replace with better transcript
const BlurredTranscript = () => (
  <div
    className="border border-gray-200 rounded-lg overflow-scroll flex flex-col items-start text-left p-4 space-y-3 mb-3 select-none"
    style={{maxHeight: '50vh'}}
  >
    <div className="blur-sm whitespace-pre-line">
      {`Speaker 0: This is an example transcript, sorry! Hello?

      Speaker 1: Hello?

      Speaker 0: Hey. Hi, my name is Jacob.

      Speaker 1: Hi, Jacob. How are you? Did I pronounce your name right?

      Speaker 0: Yeah. Jacob is right.

      Speaker 1: Perfect. Perfect. How are you doing?

      Speaker 0: I'm doing good. This is actually the first time I'm joining this particular format on Aced. Do you know, is this your first session, or have you done it before?

      Speaker 1: No. I've done it a couple of times.

      Speaker 0: Okay. Cool. So do we just, like, give questions to each other and, like, put a button

      Speaker 1: So what we do is usually, like, a thirty minute interview inside. I don't know how to do that. Sorry about keeping your mind. We just started talking. Okay.

      So right. So what we do is we set up, like, thirty minutes for each interview, depending upon what you wanna practice. Either one can go first, and then we do, like, a feedback session for the, like, whatever. So some people like to do feedback question on question basis, or we can do feedback at the end of thirty minute interview.

      Speaker 0: Okay. Sounds good. Yeah. Do you wanna do, like, a quick introduction on, like, what your focus is right now based on that? And then we can I I can go as well?

      Speaker 1: Yeah. So I have, around four years of experience in product, and I work mostly with early stage, startups. I've been a founder myself. And right now, I'm interviewing with a few, again, early stage companies. And with some of them, I'm, like, in the second round, and it's it's mostly I'm looking to practice around the product sense questions.

      Speaker 0: Okay.

      Speaker 1: Because it's sometimes, like, I I just find myself, like, sort of stuck in a loop during the interview.

      Speaker 0: Yeah.

      Speaker 1: And I wanna be familiar with, you know, those kind of questions. How about yourself?

      Speaker 0: A little similar. I have about four plus years of experience. I've, in Amazon currently. So kind of similar, you know, start looking to interview in a couple of weeks. Wanna focus on product sense as well.

      That's definitely one place where I feel, yeah, I get I feel like I can go deeper potentially on some of those ideas, and that's a place I'm trying to, like, get better at.

      Speaker 1: Amazing. Where are you based?

      Speaker 0: San Francisco is where I work out of. But because of COVID, I've temporarily moved to Arizona with my family.

      Speaker 1: Amazing. Yeah.

      Speaker 0: How about you?

      Speaker 1: Up north. Brooklyn. So

      Speaker 0: Oh, okay. Nice. Yep.

      Speaker 1: Cool. Alright. So do you wanna go first?

      Speaker 0: Yeah. I I can go first.

      Speaker 1: Okay. Just give me one minute to start.

      Speaker 0: Yeah. And maybe we can do, like, the mock for, like, twenty five minutes. And then at the end of twenty five, I'll I'll get feedback so I get a feel of the actual interview.`}
    </div>
  </div>
);

const Upsell = ({children}) => (
  <div className="flex justify-between items-center bg-indigo-100 py-2 px-4 mb-4 rounded-lg text-indigo font-semibold">
    {children}
  </div>
);

const CoachingUpsell = ({session}) => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();

  return !shouldHideCoaching(currentUser) && (
    <Upsell>
      Want more feedback from an expert interviewer?
      <Button
        href={getCoachingUrl(session)}
        onClick={() => tracker.duetFeedbackClickCoachingUpsell({type: session.type})}
        target="_blank"
      >
        Try coaching
      </Button>
    </Upsell>
  );
};

export const AIUpsell = ({session}) => {
  const {tracker} = useAnalytics();

  return (
    <Upsell>
      Want to unlock unlimited AI interviewing?
      <Button
        href="/upgrade?src=duet-1p-ai"
        onClick={() => tracker.duetClick1PAiUpsell({type: session?.type})}
      >
        Upgrade
      </Button>
    </Upsell>
  );
};

export const RecommendedContent = ({session}) => {
  const {tracker} = useAnalytics();
  const {recommendedContent: content} = session;

  const isQuestion = content?.type === 'question';
  const isLesson = content?.type === 'lesson';
  if (!isQuestion && !isLesson) return null;

  const title = isQuestion ? (content.data?.title || content.slug) : content.title;
  const href = isQuestion
    ? `/questions/${content.id}/${content.slug}`
    : `/courses/${content.course?.slug}/${content.id}`;

  const hasVideo = isQuestion && Boolean(content.data?.video?.id);
  const thumbnailLesson = isQuestion ? {video: content.data?.video?.id} : content;
  const thumbnailUrl = getResizedLessonThumbnail(thumbnailLesson, 240, 160);

  return (
    <>
      <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mt-4">
        <SparklesIcon className="h-5 w-5 text-indigo-500" aria-hidden="true" />
        <span>{isQuestion ? 'See an expert answer' : 'Recommended for you'}</span>
      </h3>
      <Link
        className="mt-2 group flex items-center justify-between gap-3 p-4 rounded-xl bg-white shadow-sm border border-gray-200 cursor-pointer transition"
        href={href}
        target="_blank"
        onClick={() => tracker.duetClickRecommendedContent({
          contentType: content.type,
          type: session?.type,
          slug: isQuestion ? content.slug : content.id,
        })}
      >
        <div className="flex-1 min-w-0">
          <p className={cx(
            'text-base leading-snug text-pretty text-gray-900',
            isQuestion ? 'font-semibold' : 'font-medium',
          )}
          >
            {title}
            {hasVideo && (
              <span className="whitespace-nowrap">
                {' '}
                <VideoBadge className="ml-1 -mt-0.5 align-middle" />
              </span>
            )}
          </p>

          {isQuestion
            ? <MetaRow question={content} />
            : (
              <span className="block text-sm text-gray-500 mt-1">
                Watch now &amp; sharpen your interview skills
              </span>
            )}
        </div>

        {thumbnailUrl && (
          <LessonThumbnail
            imageUrl={thumbnailUrl}
            className="flex-shrink-0 rounded-lg shadow-sm w-24 h-16 object-cover"
          />
        )}
      </Link>
    </>
  );
};

const SingleUserAIFeedback = ({label, session, feedback}) => (
  <div className="mb-4">
    {label && <h2 className="text-xl font-semibold text-gray-900 mb-2">{label}</h2>}
    <h3 className="text-base font-semibold text-gray-900">Question</h3>
    <div className="text-gray-500 mb-3">
      {feedback.question}
    </div>
    <h3 className="text-base font-semibold text-gray-900">Summary</h3>
    <div className="text-gray-500 mb-3">
      {feedback.summary}
    </div>
    {getFeedbackFields(session).map((field) => (field.key in feedback.attributes && (
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">{field.feedbackLabel}</h3>
        <ScoreBar score={feedback.attributes[field.key].score} />
        <p className="text-gray-500">
          {feedback.attributes[field.key].explanation}
        </p>
        {feedback.attributes[field.key].excerpt && (
          <p className="text-gray-600 italic mt-2 bg-gray-50 border-l-4 border-indigo-400 px-3 py-1 rounded">
            {feedback.attributes[field.key].excerpt}
          </p>
        )}
      </div>
    )))}

    <RecommendedContent session={session} />
  </div>
);

const RateFeedback = ({session}) => {
  const {tracker} = useAnalytics();

  const onRateAIFeedback = (rating) => {
    rateAIFeedback(session.room, {
      mode: session.mode,
      rating,
    });
    tracker.duetFeedbackRateAiFeedback({
      room: session.room,
      mode: session.mode,
      rating,
    });
  };

  const onClickSubmitFeedback = (feedback) => {
    rateAIFeedback(session.room, {
      mode: session.mode,
      feedback,
    });
  };

  return (
    <RatingWidget
      onRate={onRateAIFeedback}
      onSubmitFeedback={onClickSubmitFeedback}
      showFeedbackButton={false}
      className="w-full font-semibold text-gray-500"
    />
  );
};


const P2PPeerFeedbackTab = ({session}) => {
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.duetFeedbackViewPeerTab({room: session.room});
  }, []);

  if (session.mode === InterviewModes.Scheduled && _.isEmpty(session.feedback)) {
    return (
      <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
        <PencilAltIcon className="h-10 w-10 mb-1" />
        <span className="text-gray-900 font-semibold">Your partner is waiting on your feedback</span>
        <span className="md:w-3/4 text-center mb-4">
          In order to receive feedback, you must submit your own first.
        </span>
        <Button
          href={`/practice/feedback/${session.room}`}
          onClick={() => tracker.duetFeedbackClickSubmitFeedback({room: session.room})}
          size={ButtonSizes.LARGE}
        >
          Submit feedback
        </Button>
      </div>
    );
  }

  if (_.isEmpty(session.partnerFeedback)) {
    return (
      <>
        <CoachingUpsell session={session} />

        <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
          <EyeOffIcon className="h-10 w-10 mb-1" />
          <span className="text-gray-900 font-semibold">Peer feedback not available yet</span>
          <span className="md:w-3/4 text-center">
            Sorry, we&apos;re still waiting for your partner to submit feedback. Check back soon!
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <CoachingUpsell session={session} />

      <div
        className="border border-gray-200 rounded-lg overflow-scroll flex flex-col items-start text-left p-4 space-y-3"
        style={{maxHeight: '50vh'}}
      >
        {/* Some interview types have multiple versions of fields, so exclude deprecated */}
        {getFeedbackFields(session).map((field) => (field.key in session.partnerFeedback && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {field.feedbackLabel}
          </h3>
          {field.stars && (
            <StarRating
              initialRating={session.partnerFeedback[field.key]}
              count={field.stars.length}
              tooltipArray={field.stars}
              displayOnly
            />
          )}
          {field.written && (
            <p className="text-base">
              {session.partnerFeedback[field.key]}
            </p>
          )}
        </div>
        )))}
      </div>
    </>
  );
};

const P2PAIFeedbackTab = ({session}) => {
  const {hasAiAccess} = useAppContext();
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.duetFeedbackViewAiTab({
      room: session.room,
      mode: session.mode,
      status: session.aiFeedbackStatus,
    });
  }, []);

  if (!hasAiAccess) {
    const feedbackAvailable = [AIFeedbackStatus.PENDING, AIFeedbackStatus.COMPLETE]
      .includes(session.aiFeedbackStatus);

    return (
      <>
        <Upsell>
          {feedbackAvailable ? 'Upgrade to the Expert Membership to unlock your AI feedback' : 'Get instant feedback on your next interview with AI'}
          <Button
            href="/upgrade?src=practice-feedback-modal-ai"
            onClick={() => tracker.duetFeedbackClickAiUpsell({type: session.type})}
          >
            Upgrade now
          </Button>
        </Upsell>

        <BlurredAIFeedback />
      </>
    );
  }

  return (
    <>
      <CoachingUpsell session={session} />

      {session.aiFeedbackStatus === AIFeedbackStatus.PENDING && (
        <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
          <RefreshIcon className="h-10 w-10 mb-1" />
          <span className="text-gray-900 font-semibold">AI Feedback is processing...</span>
          <span className="md:w-3/4 text-center">
            We&apos;re still working on generating your AI feedback. Check back soon!
          </span>
        </div>
      )}

      {session.aiFeedbackStatus === AIFeedbackStatus.COMPLETE && (
        <>
          <div
            className="border border-gray-200 rounded-lg overflow-scroll flex flex-col items-start text-left p-4 space-y-3 mb-3"
            style={{maxHeight: '50vh'}}
          >
            <SingleUserAIFeedback
              label="Interview 1"
              session={session}
              feedback={session.aiFeedback.speaker_0_feedback}
            />
            <SingleUserAIFeedback
              label="Interview 2"
              session={session}
              feedback={session.aiFeedback.speaker_1_feedback}
            />
          </div>
        </>
      )}

      {(!session.aiFeedbackStatus || session.aiFeedbackStatus === AIFeedbackStatus.UNAVAILABLE) && (
        <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
          <BanIcon className="h-10 w-10 mb-1" />
          <span className="text-gray-900 font-semibold">Feedback is not available</span>
          <span className="md:w-3/4 text-center">
            Sorry, AI feedback is unavailable for this session.
            You or your partner may have had the feature disabled.
          </span>
        </div>
      )}
    </>
  );
};

const AIFeedbackTab = ({session}) => {
  const {hasAiAccess} = useAppContext();
  const {tracker} = useAnalytics();

  useEffect(() => {
    tracker.duetFeedbackViewAiTab({
      room: session.room,
      mode: session.mode,
      status: session.aiFeedbackStatus,
    });
  }, []);

  return (
    <>
      {hasAiAccess
        ? <CoachingUpsell session={session} />
        : <AIUpsell session={session} />}

      {session.aiFeedbackStatus === AIFeedbackStatus.PENDING && (
        <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
          <div className="relative h-10 w-10 mb-4">
            <Spinner className="border-3" />
          </div>
          <span className="text-gray-900 font-semibold">AI feedback is processing...</span>
          <span className="md:w-3/4 text-center">
            Your feedback and transcript will be available in just a moment!
          </span>
        </div>
      )}

      {session.aiFeedbackStatus === AIFeedbackStatus.COMPLETE && (
        <div
          className="border border-gray-200 rounded-lg overflow-scroll flex flex-col items-start text-left p-4 space-y-3 mb-3"
          style={{maxHeight: '50vh'}}
        >
          <SingleUserAIFeedback
            session={session}
            feedback={session.aiFeedback}
          />
        </div>
      )}

      {(!session.aiFeedbackStatus || session.aiFeedbackStatus === AIFeedbackStatus.UNAVAILABLE) && (
        <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
          <BanIcon className="h-10 w-10 mb-1" />
          <span className="text-gray-900 font-semibold">Feedback is not available</span>
          <span className="md:w-3/4 text-center">
            Sorry, AI feedback is unavailable for this session.
          </span>
        </div>
      )}
    </>
  );
};

export const PendingTranscript = () => (
  <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
    <RefreshIcon className="h-10 w-10 mb-1" />
    <span className="text-gray-900 font-semibold">Transcript is processing...</span>
    <span className="md:w-3/4 text-center">
      We&apos;re still working on generating your transcript. Check back soon!
    </span>
  </div>
);

export const CompleteTranscript = ({session}) => {
  if (typeof session.transcript === 'object') {
    return (
      <p
        className="border border-gray-200 rounded-lg overflow-scroll p-4 mb-3 text-base whitespace-pre-line"
        style={{maxHeight: '50vh'}}
      >
        <TranscriptItems transcript={session.transcript} />
      </p>
    );
  }

  if (typeof session.transcript === 'string') {
    const speakerLabels = ['Speaker 0:', 'Speaker 1:'];
    const OneIndexedSpeakerLabels = {
      'Speaker 0:': 'Speaker 1:',
      'Speaker 1:': 'Speaker 2:',
    };

    // Convert string into an array of parts
    const parts = session.transcript.trim().split(new RegExp(`(${speakerLabels.join('|')})`, 'g'));

    return (
      <p
        className="border border-gray-200 rounded-lg overflow-scroll p-4 mb-3 text-base whitespace-pre-line"
        style={{maxHeight: '50vh'}}
      >
        {parts.map((part, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={idx}>
            {speakerLabels.includes(part)
              ? <span className="font-semibold">{OneIndexedSpeakerLabels[part]}</span>
              : part}
          </React.Fragment>
        ))}
      </p>
    );
  }

  return null;
};

const UnavailableTranscript = () => (
  <div className="flex flex-col items-center text-gray-500 mt-10 mb-16">
    <BanIcon className="h-10 w-10 mb-1" />
    <span className="text-gray-900 font-semibold">Transcript is not available</span>
    <span className="md:w-3/4 text-center">
      Sorry, your transcript is unavailable for this session.
      You or your partner may have had the feature disabled.
    </span>
  </div>
);


const P2PTranscriptTab = ({session}) => {
  const {hasAiAccess} = useAppContext();
  const {tracker} = useAnalytics();
  const status = session.transcriptStatus;

  useEffect(() => {
    tracker.duetFeedbackViewTranscriptTab({
      room: session.room,
      mode: session.mode,
      status,
    });
  }, []);

  if (!hasAiAccess) {
    const transcriptAvailable = [AIFeedbackStatus.PENDING, AIFeedbackStatus.COMPLETE]
      .includes(session.aiFeedbackStatus);

    return (
      <>
        <Upsell>
          {transcriptAvailable ? 'Upgrade to the Expert Membership to unlock your transcript' : 'Get your transcript and AI feedback on your next interview'}
          <Button
            href="/upgrade?src=practice-feedback-modal-ai"
            onClick={() => tracker.duetFeedbackClickAiUpsell({type: session.type})}
          >
            Upgrade now
          </Button>
        </Upsell>

        <BlurredTranscript />
      </>
    );
  }

  return (
    <>
      <CoachingUpsell session={session} />
      {status === AIFeedbackStatus.PENDING && <PendingTranscript />}
      {status === AIFeedbackStatus.COMPLETE && <CompleteTranscript session={session} />}
      {(!status || status === AIFeedbackStatus.UNAVAILABLE) && <UnavailableTranscript />}
    </>
  );
};


const AITranscriptTab = ({session}) => {
  const {hasAiAccess} = useAppContext();
  const {tracker} = useAnalytics();
  const status = session.transcriptStatus;

  useEffect(() => {
    tracker.duetFeedbackViewTranscriptTab({
      room: session.room,
      mode: session.mode,
      status,
    });
  }, []);

  return (
    <>
      {hasAiAccess
        ? <CoachingUpsell session={session} />
        : <AIUpsell session={session} />}
      {status === AIFeedbackStatus.PENDING && <PendingTranscript />}
      {status === AIFeedbackStatus.COMPLETE && <CompleteTranscript session={session} />}
      {(!status || status === AIFeedbackStatus.UNAVAILABLE) && <UnavailableTranscript />}
    </>
  );
};

const Checkbox = ({checked, onChange, children}) => (
  <label className="flex items-center font-medium text-gray-600">
    <StyledCheckbox
      checked={checked}
      onChange={onChange}
      className="mr-2"
    />
    {children}
  </label>
);

export const UserFeedbackForm = ({
  session,
  header = 'How was your interview session?',
  subheader,
  isOpen,
  onClose,
  onSkip,
  onSubmit,
  showSkipButton,
}) => {
  const [rating, setRating] = useState();
  const [positiveFeedback, setPositiveFeedback] = useState('');
  const [negativeFeedback, setNegativeFeedback] = useState('');
  const [issues, setIssues] = useState({
    interruptions: false,
    latency: false,
    audioQuality: false,
    aiResponseQuality: false,
    other: false,
  });
  const {tracker} = useAnalytics();

  const toggleRating = (value) => {
    setRating((prev) => (prev === value ? undefined : value));
  };

  const toggleIssue = (issue) => {
    setIssues((prev) => ({
      ...prev,
      [issue]: !prev[issue],
    }));
  };

  const handleSubmit = async () => {
    const feedbackData = {
      mode: session.mode,
      rating,
    };

    if (rating === 'up' && positiveFeedback.trim()) {
      feedbackData.feedback = positiveFeedback.trim();
    }

    if (rating === 'down') {
      const selectedIssues = _.pickBy(issues, Boolean);
      if (!_.isEmpty(selectedIssues)) {
        feedbackData.issues = selectedIssues;
      }
      if (negativeFeedback.trim()) {
        feedbackData.feedback = negativeFeedback.trim();
      }
    }

    tracker.duetFeedbackRateAiFeedback({
      room: session.room,
      mode: session.mode,
      rating: feedbackData.rating,
    });

    onSubmit();

    await rateAIFeedback(session.room, feedbackData);
  };

  return (
    <Modal
      header={header}
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton
      modalClassName="max-w-xl"
    >
      {subheader && (
        <div className="flex justify-center text-center -mt-4 mb-3 font-semibold text-gray-500">
          {subheader}
        </div>
      )}

      <div className="my-8 flex justify-center space-x-6">
        <button
          type="button"
          onClick={() => toggleRating('up')}
          className={cx('border-2 rounded-full p-6 transition', {
            'border-green-500 bg-green-50': rating === 'up',
            'border-gray-300': rating !== 'up',
          })}
          aria-label="Rate experience as positive"
          aria-pressed={rating === 'up'}
        >
          <ThumbUpIcon className={cx('h-6 w-6 transition', {
            'text-green-500': rating === 'up',
            'text-gray-600': rating !== 'up',
          })}
          />
        </button>
        <button
          type="button"
          onClick={() => toggleRating('down')}
          className={cx('border-2 rounded-full p-6 transition', {
            'border-red-500 bg-red-50': rating === 'down',
            'border-gray-300': rating !== 'down',
          })}
          aria-label="Rate experience as negative"
          aria-pressed={rating === 'down'}
        >
          <ThumbDownIcon className={cx('h-6 w-6 transition', {
            'text-red-500': rating === 'down',
            'text-gray-600': rating !== 'down',
          })}
          />
        </button>
      </div>

      {rating === 'up' && (
        <div className="my-8 ml-2">
          <label htmlFor="positive-feedback" className="font-semibold text-gray-500 mb-1 block">
            Any feedback or comments? (optional)
          </label>
          <StyledTextArea
            id="positive-feedback"
            placeholder="Share your thoughts..."
            rows={4}
            value={positiveFeedback}
            onChange={(e) => setPositiveFeedback(e.target.value)}
          />
        </div>
      )}

      {rating === 'down' && (
        <div className="my-8 ml-2">
          <div className="font-semibold text-gray-500 mb-1">
            What issues did you experience? (select all that apply)
          </div>

          <Checkbox
            checked={issues.interruptions}
            onChange={() => toggleIssue('interruptions')}
          >
            Interruptions during speech
          </Checkbox>
          <Checkbox
            checked={issues.latency}
            onChange={() => toggleIssue('latency')}
          >
            Latency or slow responses
          </Checkbox>
          <Checkbox
            checked={issues.audioQuality}
            onChange={() => toggleIssue('audioQuality')}
          >
            Audio quality issues
          </Checkbox>
          <Checkbox
            checked={issues.aiResponseQuality}
            onChange={() => toggleIssue('aiResponseQuality')}
          >
            AI response quality issues
          </Checkbox>
          <Checkbox
            checked={issues.other}
            onChange={() => toggleIssue('other')}
          >
            Other
          </Checkbox>

          <label htmlFor="negative-feedback" className="font-semibold text-gray-500 mb-1 mt-4 block">
            Additional comments (optional)
          </label>
          <StyledTextArea
            id="negative-feedback"
            placeholder="Share your thoughts..."
            rows={4}
            value={negativeFeedback}
            onChange={(e) => setNegativeFeedback(e.target.value)}
          />
        </div>
      )}

      <div className="flex justify-between flex-col-reverse sm:flex-row">
        {showSkipButton ? (
          <button
            type="button"
            onClick={onSkip}
            className="font-medium text-gray-500 px-3 py-2"
          >
            Skip
          </button>
        ) : <div />}

        <Button
          onClick={handleSubmit}
          disabled={!rating}
        >
          Submit
        </Button>
      </div>
    </Modal>
  );
};

export const AIFeedbackModal = ({
  session,
  isOpen,
  onClose,
  bottomContent = null,
  initialTab = TabKeys.AI,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (isOpen) {
      const tab = Object.values(TabKeys).includes(initialTab) ? initialTab : TabKeys.AI;
      setActiveTab(tab);
    }
  }, [isOpen]);

  return (
    <Modal
      header="Feedback for your interview"
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton
      modalClassName="max-w-2xl"
    >
      <div className="flex justify-center text-center -mt-4 mb-3 font-semibold text-gray-500">
        {`${toDuetInterviewTypeName(session.type)}, ${getDisplayTimeForPreviousDuetSession(session)}`}
      </div>

      <Tabs
        tabs={[AITab, TranscriptTab]}
        active={activeTab}
        onClick={setActiveTab}
        className="-mx-4 px-4 mb-4"
      />

      {activeTab === TabKeys.AI && <AIFeedbackTab session={session} />}
      {activeTab === TabKeys.TRANSCRIPT && <AITranscriptTab session={session} />}

      {session.aiFeedbackStatus === AIFeedbackStatus.COMPLETE && (
        <div className="flex justify-between flex-col-reverse sm:flex-row">
          <RateFeedback session={session} />

          {bottomContent}
        </div>
      )}
    </Modal>
  );
};

export const P2PFeedbackModal = ({
  isOpen,
  onClose,
  session,
  initialTab = TabKeys.PEER,
}) => {
  const {currentUser} = useAppContext();
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (isOpen) {
      const tab = Object.values(TabKeys).includes(initialTab) ? initialTab : TabKeys.PEER;
      setActiveTab(tab);
    }
  }, [isOpen]);

  return (
    <Modal
      header="Feedback for your interview"
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton
      modalClassName="max-w-2xl"
    >
      <div className="flex justify-center text-center -mt-4 mb-3 font-semibold text-gray-500">
        {`${toDuetInterviewTypeName(session.type)}, ${getDisplayTimeForPreviousDuetSession(session)}`}
      </div>

      {hideAIFeedbackFeatures(currentUser, session)
        ? <P2PPeerFeedbackTab session={session} />
        : (
          <>
            <Tabs
              tabs={[PeerTab, AITab, TranscriptTab]}
              active={activeTab}
              onClick={setActiveTab}
              className="-mx-4 px-4 mb-4"
            />

            {activeTab === TabKeys.PEER && <P2PPeerFeedbackTab session={session} />}
            {activeTab === TabKeys.AI && <P2PAIFeedbackTab session={session} />}
            {activeTab === TabKeys.TRANSCRIPT && <P2PTranscriptTab session={session} />}

            <RateFeedback session={session} />
          </>
        )}
    </Modal>
  );
};

const DuetFeedbackModal = (props) => {
  if ([
    InterviewModes.Scheduled,
    InterviewModes.OnDemand,
    InterviewModes.Pramp,
  ].includes(props.session?.mode)) {
    return <P2PFeedbackModal {...props} />;
  }

  if (props.session?.mode === InterviewModes.AI) return <AIFeedbackModal {...props} />;

  return null;
};

export default DuetFeedbackModal;
